/**
 * <s-pack-popup> — JOIN THE PACK signup dialog.
 *
 * Timing: waits until the intro loader is gone (html[data-intro-active] removed), then data-delay ms.
 * Never pops while another modal <dialog> (cart drawer, mobile menu) is open; it waits and retries.
 * Frequency (localStorage "bolox-pack"): shown once, closing snoozes data-snooze-days, signup = never again.
 * Close: X button / success button (commands), Esc (native), click on the backdrop area outside the panel.
 * Consent: the phone input has no name. Only when the SMS box is checked does the number travel, as a
 * consent record in contact[note] plus the sms-optin-requested tag.
 */
const KEY = 'bolox-pack';
const FLAG = 'bolox-pack-submitting';

class SPackPopup extends HTMLElement {
  #ac;
  #dialog;
  #timer = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const form = this.querySelector('form');
    const posted = this.querySelector('[data-pack-posted]')?.dataset.packPosted || '';
    const submitting = this.#session(FLAG);
    this.#session(FLAG, null);

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (this.#read() !== 'subscribed') this.#snooze();
    }, opts);

    const sms = this.querySelector('[data-pack-sms]');
    const phone = this.querySelector('[data-pack-phone]');
    sms?.addEventListener('change', () => {
      if (sms.checked) this.querySelector('[data-pack-phone-error]')?.classList.add('hidden');
    }, opts);

    form?.addEventListener('submit', (e) => this.#onSubmit(e, form, sms, phone), opts);

    // Server round trip from a customer form post.
    if (posted === 'success') {
      this.#write('subscribed');
      if (submitting) this.#open('success');
      return;
    }
    if (posted === 'error' && submitting) {
      this.#open('form');
      return;
    }

    if (this.#shouldShow()) this.#afterIntro(() => this.#schedule());
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #onSubmit(e, form, sms, phone) {
    const email = form.querySelector('input[type="email"]');
    if (!email.checkValidity()) {
      e.preventDefault();
      email.reportValidity();
      return;
    }
    const tags = form.querySelector('[data-pack-tags]');
    const note = form.querySelector('[data-pack-note]');
    const number = phone?.value.trim() || '';
    const phoneError = form.querySelector('[data-pack-phone-error]');

    if (sms?.checked) {
      if (number.replace(/\D/g, '').length < 10) {
        e.preventDefault();
        phoneError?.classList.remove('hidden');
        phone.focus();
        return;
      }
      const consent = form.querySelector('[data-pack-consent-text]')?.textContent.replace(/\s+/g, ' ').trim() || '';
      tags.value = 'popup-signup,sms-optin-requested';
      note.disabled = false;
      note.value = `SMS marketing opt-in requested via site popup. Phone: ${number}. Checkbox checked by visitor at ${new Date().toISOString()}. Consent text shown: "${consent}"`;
    } else {
      tags.value = 'popup-signup';
      note.disabled = true;
      note.value = '';
    }
    this.#session(FLAG, '1');
  }

  #shouldShow() {
    const state = this.#read();
    if (state === 'subscribed') return false;
    const until = Number(state);
    return !(until && Date.now() < until);
  }

  #afterIntro(cb) {
    const root = document.documentElement;
    if (!root.hasAttribute('data-intro-active') && !document.getElementById('intro-loader')) return cb();
    const check = () => {
      if (root.hasAttribute('data-intro-active') || document.getElementById('intro-loader')) return false;
      observer.disconnect();
      cb();
      return true;
    };
    const observer = new MutationObserver(check);
    observer.observe(root, { attributes: true, attributeFilter: ['data-intro-active'], childList: true, subtree: true });
    this.#ac.signal.addEventListener('abort', () => observer.disconnect());
    check();
  }

  #schedule(delay = Number(this.dataset.delay) || 5000) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      if (!this.#shouldShow()) return;
      if (this.#otherModalOpen()) return this.#schedule(1500);
      this.#open('form');
      this.#snooze();
    }, delay);
  }

  #otherModalOpen() {
    return [...document.querySelectorAll('dialog[open]')].some((d) => d !== this.#dialog);
  }

  #open(view) {
    if (this.#dialog.open) return;
    const formView = this.querySelector('[data-pack-view="form"]');
    const successView = this.querySelector('[data-pack-view="success"]');
    formView.hidden = view === 'success';
    successView.hidden = view !== 'success';
    this.#dialog.showModal();
    if (view === 'success') successView.focus();
    else this.querySelector('#pack-popup-email')?.focus();
  }

  #snooze() {
    const days = Number(this.dataset.snoozeDays) || 14;
    this.#write(String(Date.now() + days * 864e5));
  }

  #read() {
    try { return localStorage.getItem(KEY); } catch { return null; }
  }

  #write(value) {
    try { localStorage.setItem(KEY, value); } catch { /* storage blocked: popup just won't remember */ }
  }

  #session(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(key);
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch { return null; }
    return null;
  }
}

if (!customElements.get('s-pack-popup')) customElements.define('s-pack-popup', SPackPopup);
